import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  ListItemButton
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  Assignment as ProjectIcon,
  Assessment as ReportIcon,
  RemoveRedEye as VisualizerIcon,
  Warning as RiskIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  Person as PersonIcon,
  Star as StarIcon
} from '@mui/icons-material';

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // State for admin menu (desktop)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
    setDrawerOpen(false);
  };

  const menuItems = [
    { label: 'Projects', path: '/projects', icon: <ProjectIcon /> },
    { label: 'Visualizer', path: '/project-visualizer', icon: <VisualizerIcon /> },
    { label: 'Risks', path: '/risks', icon: <RiskIcon /> },
    { label: 'Dashboard', path: '/project-dashboard', icon: <DashboardIcon /> },
    { label: 'Reports', path: '/reports', icon: <ReportIcon /> },
  ];

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0f172a', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Toolbar sx={{ justifyContent: 'space-between', height: { xs: 70, sm: 90 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer(true)}
                sx={{ mr: 2, color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none' 
              }}
            >
              <Box 
                component="img" 
                src="/zandrigo_futuristic_optimized_dark_bg.svg?v=11" 
                alt="Zandrigo Logo" 
                sx={{ height: { xs: 55, sm: 68 } }} 
              />
            </Box>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button 
                  key={item.path}
                  color="inherit" 
                  component={Link} 
                  to={item.path}
                  sx={{ 
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.7)',
                    px: 2,
                    '&:hover': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {isAuthenticated && user?.role === 'viewer' && (
                <Button 
                  variant="contained" 
                  color="secondary" 
                  component={Link} 
                  to="/subscription"
                  startIcon={<StarIcon />}
                  sx={{ 
                    fontWeight: 800, 
                    px: 3, 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #f59e0b 30%, #fbbf24 90%)',
                    boxShadow: '0 3px 5px 2px rgba(245, 158, 11, .3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #d97706 30%, #f59e0b 90%)',
                    }
                  }}
                >
                  Upgrade to Pro
                </Button>
              )}
                     
              {isAuthenticated ? (
                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<PersonIcon />}
                    onClick={handleMenuClick}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ fontWeight: 700, borderRadius: 2, color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                  >
                    {user?.username}
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      sx: { mt: 1.5, minWidth: 180, borderRadius: 2, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }
                    }}
                  >
                    <MenuItem onClick={() => handleNavigate('/projects')}>
                      <ListItemIcon><ProjectIcon fontSize="small" /></ListItemIcon>
                      My Projects
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={logout} sx={{ color: 'error.main' }}>
                      <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary" 
                  component={Link} 
                  to="/login"
                  startIcon={<LoginIcon />}
                  sx={{ fontWeight: 700, px: 3, borderRadius: 2 }}
                >
                  Login
                </Button>
              )}
            </Box>
          )}
          
          {isMobile && isAuthenticated && (
            <IconButton color="primary" onClick={handleMenuClick} sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)' }}>
              <PersonIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{ sx: { width: 300, bgcolor: '#0f172a', color: 'white', borderTopRightRadius: 20, borderBottomRightRadius: 20 } }}
      >
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box 
            component="img" 
            src="/zandrigo_futuristic_optimized_dark_bg.svg?v=11" 
            alt="Zandrigo Logo" 
            sx={{ height: 64 }} 
          />
        </Box>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ px: 2, py: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
              <ListItemButton onClick={() => handleNavigate(item.path)} sx={{ borderRadius: 2, py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'primary.light' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, color: 'white' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mt: 'auto', bgcolor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ px: 2, py: 2 }}>
          {isAuthenticated ? (
            <ListItem disablePadding>
              <ListItemButton onClick={logout} sx={{ borderRadius: 2, color: '#f87171', py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          ) : (
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/login')} sx={{ borderRadius: 2, color: 'primary.light', py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;